import { Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { deliveryChargesService } from '../services/deliveryChargesService';
import useToastStore from '../store/useToastStore';
import { useDeliveryCharges } from '../hooks/deliverycharges/useDeliveryCharges';

const DeliveryChargesManagement: React.FC = () => {
  const [flatDeliveryFee, setFlatDeliveryFee] = useState<number>(0);
  const [freeDeliveryThreshold, setFreeDeliveryThreshold] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToastStore();
  const { data, isLoading } = useDeliveryCharges();


  useEffect(() => {
    if (data) {
      setFlatDeliveryFee(data.flatFee || 0);
      setFreeDeliveryThreshold(data.freeThreshold || 0);
    }
  }, [data]);


  const handleSaveSettings = async () => {
    if (flatDeliveryFee < 0) {
      toast.showToast('Delivery fee cannot be negative', 'error');
      return;
    }
    if (freeDeliveryThreshold < 0) {
      toast.showToast('Free delivery threshold cannot be negative', 'error');
      return;
    }

    try {
      setIsSaving(true);
      await deliveryChargesService.updateDeliverySettings({
        flatFee: flatDeliveryFee,
        freeThreshold: freeDeliveryThreshold
      });
      toast.showToast('Delivery settings updated successfully', 'success');
    } catch (error) {
      console.error('Error updating delivery settings:', error);
      toast.showToast('Failed to update delivery settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="space-y-8">

          {/* Flat Delivery Fee */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Flat Delivery Fee</h2>
            <p className="text-sm text-gray-600">
              Set a fixed delivery charge that applies to all orders.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Fee (₹)</label>
                <input
                  type="number"
                  value={flatDeliveryFee || ''}
                  onChange={(e) => setFlatDeliveryFee(Number(e.target.value) || 0)}
                  placeholder="Enter delivery fee"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Free Delivery Threshold */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Free Delivery Threshold</h2>
            <p className="text-sm text-gray-600">
              Orders above this amount will have free delivery. Set to 0 to disable free delivery.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">Order Amount (₹)</label>
                <input
                  type="number"
                  value={freeDeliveryThreshold || ''}
                  onChange={(e) => setFreeDeliveryThreshold(Number(e.target.value) || 0)}
                  placeholder="Enter threshold amount"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-900">Summary</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>• All orders will be charged ₹{flatDeliveryFee} for delivery</p>
              {freeDeliveryThreshold > 0 ? (
                <p>• Orders above ₹{freeDeliveryThreshold} will have free delivery</p>
              ) : (
                <p>• No free delivery threshold set</p>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-start">
            <button
              onClick={handleSaveSettings}
              disabled={isSaving}
              className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryChargesManagement; 